import { useNavigate, useParams } from 'react-router-dom';

import { ManagerProgressionTaskSlug } from '@sorare/core/src/__generated__/globalTypes';
import {
  FOOTBALL_PLAY,
  FOOTBALL_PLAY_RIVALS_MATCHES,
} from '@sorare/core/src/constants/__generated__/routes';
import {
  ANY_SPORT_DRAFT,
  ANY_SPORT_MARKET,
  ANY_SPORT_MARKET_NEW_SIGNINGS,
  ANY_SPORT_MY_CLUB_COLLECTIONS,
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_PRO,
  INVITE,
} from '@sorare/core/src/constants/routes';
import {
  LearnCompetitionsOnboardingStep,
  MarketplaceOnboardingStep,
  useManagerTaskContext,
} from '@sorare/core/src/contexts/managerTask';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useWalletDrawerContext } from '@sorare/core/src/contexts/walletDrawer';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

export const LINKS_PER_TASK: Partial<
  Record<ManagerProgressionTaskSlug, { to: string }>
> = {
  [ManagerProgressionTaskSlug.DRAFT_YOUR_TEAM]: {
    to: ANY_SPORT_DRAFT,
  },
  [ManagerProgressionTaskSlug.FIRST_COMPETITION]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LEARN_SCORING]: {
    to: 'https://sorare.com/help/a/4402897588241/how-is-the-player-score-calculated-ps',
  },
  [ManagerProgressionTaskSlug.SCOUT_PLAYERS_MARKETPLACE]: {
    to: ANY_SPORT_MARKET,
  },
  [ManagerProgressionTaskSlug.FIRST_BID]: { to: ANY_SPORT_MARKET_NEW_SIGNINGS },
  [ManagerProgressionTaskSlug.FIRST_CLAIM_REWARDS]: { to: ANY_SPORT_PLAY },
  [ManagerProgressionTaskSlug.SECOND_LINEUP]: { to: ANY_SPORT_PLAY_PRO },
  [ManagerProgressionTaskSlug.INFLUENCER_INVITE_1_FRIEND]: { to: INVITE },
  [ManagerProgressionTaskSlug.INFLUENCER_REFER_1]: { to: INVITE },
  [ManagerProgressionTaskSlug.INFLUENCER_REFER_5]: { to: INVITE },
  [ManagerProgressionTaskSlug.INFLUENCER_REFER_10]: { to: INVITE },
  [ManagerProgressionTaskSlug.FIRST_RIVALS_LINEUP]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.CHALLENGE_A_FRIEND]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WATCH_RIVALS_RECAP]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WIN_ONE_MATCH]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.PLAY_ONE_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WIN_ONE_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.BUY_ONE_LIMITED]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WIN_TWO_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_REACHED_35_LIMITED]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_REACHED_100_LIMITED]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_REACHED_250_LIMITED]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_REACHED_500_SEVERAL_ALBUMS]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_REACHED_750_SEVERAL_ALBUMS]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_TOP_LIMITED]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_TOP_25_RARE]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_TOP_RARE]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_TOP_1_ONE_ALBUM]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.COLLECTOR_TOP_1_SEVERAL_ALBUMS]: {
    to: ANY_SPORT_MY_CLUB_COLLECTIONS,
  },
  [ManagerProgressionTaskSlug.LIMITED_REACHED_100]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_REACHED_200]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_REACHED_250]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_REACHED_300]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_REACHED_300_TEN]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_WON_1]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_WON_2]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_WON_3]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_WON_4]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.LIMITED_WON_1_TROPHY]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_REACHED_100]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_REACHED_200]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_REACHED_250]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_REACHED_300]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_WON_1]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_WON_2]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_WON_3]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_WON_4]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_WON_1_TROPHY]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.RARE_WON_3_TROPHY]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_REACHED_100]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_REACHED_200]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_REACHED_250]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_REACHED_300]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_WON_1]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_WON_2]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_WON_3]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_WON_4]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_WON_1_TROPHY]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SUPER_RARE_WON_3_TROPHY]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_REACHED_100]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_REACHED_200]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_REACHED_250]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_REACHED_300]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_WON_1]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_WON_2]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_WON_3]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_WON_4]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_WON_1_TROPHY]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.UNIQUE_WON_3_TROPHY]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_100]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_200]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_200_TWICE]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_250_TWICE]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_250_FOUR_TIMES]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_300_FOUR_TIMES]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_300_SIX_TIMES]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_400_TWICE]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_REACHED_400_FOUR_TIMES]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.COMMON_WON_1_LIMITED]: {
    to: ANY_SPORT_PLAY_PRO,
  },
  [ManagerProgressionTaskSlug.SCORE_300_IN_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.PLAY_ONE_MATCH_ON_5_CONSECUTIVE_DAYS]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WIN_3_CONSECUTIVE_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WIN_ARENA_BY_LESS_THAN_5_POINTS]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.SCORE_400_IN_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WIN_5_CONSECUTIVE_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WIN_ARENA_BY_MORE_THAN_100_POINTS]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.PLAY_ONE_MATCH_ON_30_CONSECUTIVE_DAYS]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.WIN_10_CONSECUTIVE_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
  [ManagerProgressionTaskSlug.SCORE_500_IN_ARENA]: {
    to: FOOTBALL_PLAY_RIVALS_MATCHES,
  },
};

const TASKS_WITH_SPECIAL_ACTION = [
  ManagerProgressionTaskSlug.LEARN_MARKETPLACE,
  ManagerProgressionTaskSlug.LEARN_PRO_COMPETITION,
  ManagerProgressionTaskSlug.CONNECT_WALLET,
  ManagerProgressionTaskSlug.WATCH_RIVALS_RECAP,
];

export const useGetTaskAction = (
  name: ManagerProgressionTaskSlug,
  declareTask: () => void
) => {
  const { setTask, setStep, setOnSuccessCallback } = useManagerTaskContext();
  const { hideBottomNavBar, isMobileApp } = useIsMobileApp();
  const navigate = useNavigate();
  const { showDrawer } = useWalletDrawerContext();
  const { generateSportContextPath } = useSportContext();
  // assuming we are in MY_CLUB_SLUG_CHALLENGES page
  const { slug = '' } = useParams();

  const exploreMarketplace = () => {
    setTask({ id: name });
    // // For webview we skip the first task, has we display bottom bar from app.
    if (hideBottomNavBar && isMobileApp) {
      navigate(ANY_SPORT_MARKET);
      setStep(MarketplaceOnboardingStep.managerSalesLink);
    } else {
      setStep(MarketplaceOnboardingStep.menu);
    }
    setOnSuccessCallback(() => declareTask);
  };

  const exploreProCompetitions = () => {
    setTask({ id: name });
    navigate(FOOTBALL_PLAY);
    setStep(LearnCompetitionsOnboardingStep.menu);
    setOnSuccessCallback(() => declareTask);
  };

  const hasSpecialAction = TASKS_WITH_SPECIAL_ACTION.includes(name);
  const onClick = () => {
    if (name === ManagerProgressionTaskSlug.LEARN_MARKETPLACE) {
      exploreMarketplace();
    }
    if (name === ManagerProgressionTaskSlug.LEARN_PRO_COMPETITION) {
      exploreProCompetitions();
    }
    if (name === ManagerProgressionTaskSlug.CONNECT_WALLET) {
      showDrawer();
    }
  };

  const to = LINKS_PER_TASK[name]?.to;

  return {
    onClick: hasSpecialAction ? onClick : undefined,
    to:
      to &&
      generateSportContextPath(to, {
        params: {
          slug,
        },
      }),
  };
};
